import React from "react";

import { faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard } from "@performyard/py-components";

import ChecklistItem from "./ChecklistItem";
import ChecklistSection from "./ChecklistSection";

import type { ItemStatus } from "../types";

import { useAddMeetingActionItem, useEditMeetingActionItem, useMeetingActionItems } from "../../../../api/hooks";
import { useChecklistItems } from "../hooks";

import { getParticipantName } from "../utils";

import { useMeetingContext } from "../context";

/**
 * Action items section. Renders inline-editable action items in the main
 * content area, with a unified add/edit form that includes due-date and
 * assignee inline pickers.
 */
const MeetingDetailsActionItems = (): React.ReactElement => {
	const { meeting, isInteractionDisabled, userId, workspace, participantNames } = useMeetingContext();
	const { data: items, isLoading } = useMeetingActionItems(meeting._id);
	const { mutate: addItem } = useAddMeetingActionItem();
	const { mutate: editItem } = useEditMeetingActionItem();

	const {
		activeItems,
		isAddingItem,
		editingItemId,
		isAddButtonDisabled,
		handleAddButtonClick,
		handleEditStart,
		handleEditSave,
		handleNewItemSave,
		handleStatusChange,
	} = useChecklistItems({
		meetingId: meeting._id,
		currentUserId: userId,
		itemType: "action",
		items,
		isLoading,
		addItemMutation: addItem,
		editItemMutation: editItem,
		isInteractionDisabled,
	});

	const workspaceParticipants = workspace?.participants || [];

	// Per Figma, the "Add Action Item" button hides while the inline create form
	// is open — the form itself is the affordance for the in-progress add.
	const addButton =
		!isInteractionDisabled && !isAddingItem ? (
			<ButtonStandard
				iconLeft={faPlus}
				onClick={handleAddButtonClick}
				disabled={isAddButtonDisabled}
				dangerouslySetClassName={
					isAddButtonDisabled
						? "disabled:cursor-not-allowed disabled:pointer-events-auto disabled:hover:!bg-primary-5 w-fit flex-shrink-0"
						: "w-fit flex-shrink-0"
				}
			>
				Add Action Item
			</ButtonStandard>
		) : undefined;

	return (
		<ChecklistSection
			title="Action Items"
			isLoading={isLoading}
			errorMessage="Failed to load action items"
			variant="main"
			footer={addButton}
		>
			{activeItems.map((item, index) => {
				const meetingItem = item.meetings.find((m) => m.meetingId === meeting._id);
				const assignee = item.participants?.find((p) => p.role === "assignee");

				return (
					<React.Fragment key={item._id}>
						{index > 0 && <div className="h-px w-full bg-neutral-2" />}
						<ChecklistItem
							id={item._id}
							name={item.name}
							dueDate={item.dueDate ? new Date(item.dueDate) : null}
							status={(meetingItem?.status as ItemStatus) || "incomplete"}
							creatorId={item.created?.userId || ""}
							creatorName={getParticipantName(workspace, participantNames, item.created?.userId)}
							userId={userId}
							isEditing={editingItemId === item._id}
							assigneeId={assignee?.userId}
							assigneeName={getParticipantName(workspace, participantNames, assignee?.userId)}
							itemType="action"
							onEdit={handleEditStart}
							onStatusChange={(status) => handleStatusChange(item._id, status)}
							onSave={handleEditSave}
							workspaceParticipants={workspaceParticipants}
						/>
					</React.Fragment>
				);
			})}

			{isAddingItem && (
				<ChecklistItem
					id="new-action-item"
					name=""
					status="incomplete"
					creatorId={userId}
					userId={userId}
					isEditing={true}
					isNewItem={true}
					itemType="action"
					onEdit={() => {}}
					onStatusChange={() => {}}
					onSave={handleNewItemSave}
					workspaceParticipants={workspaceParticipants}
				/>
			)}
		</ChecklistSection>
	);
};

export default MeetingDetailsActionItems;
